import { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import api from '../services/api';
import EmptyState from '../components/EmptyState';
import MenuAcoes from '../components/MenuAcoes';
import { soDigitos } from '../helpers/mascaras';
import { useAuth } from '../contexts/AuthContext';

const VERDE = '#1F6A2D';
const VERMELHO = '#dc3545';
const LARANJA = '#E2901A';

export default function Arquitetos() {
  const { temPermissao } = useAuth();
  const podeEditar = temPermissao('arquitetos_editar'); // editar e-mail, resetar senha, ativar/desativar, desvincular
  const podeFilial = temPermissao('arquitetos_filial'); // preencher a filial de origem do arquiteto
  const [aba, setAba] = useState('vinculados');
  const [vinculados, setVinculados] = useState([]);
  const [disponiveis, setDisponiveis] = useState([]);
  const [filiais, setFiliais] = useState([]); // [{ codigo, nome }] — info nossa, fixa
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');

  const nomeFilial = (cod) => filiais.find(f => f.codigo === cod)?.nome || null;

  const carregarVinculados = useCallback(() => {
    setCarregando(true);
    setErro('');
    api.get('/arquitetos')
      .then(r => setVinculados(r.data))
      .catch(e => setErro(e.response?.data?.error || 'Erro ao carregar arquitetos'))
      .finally(() => setCarregando(false));
  }, []);

  const carregarDisponiveis = useCallback(() => {
    setCarregando(true);
    setErro('');
    api.get('/arquitetos/disponiveis')
      .then(r => {
        const unicos = Array.from(new Map(r.data.map(a => [a.oid, a])).values());
        setDisponiveis(unicos);
      })
      .catch(e => setErro(e.response?.data?.error || 'Erro ao carregar lista'))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => {
    if (aba === 'vinculados') carregarVinculados();
    else carregarDisponiveis();
  }, [aba, carregarVinculados, carregarDisponiveis]);

  // Filiais (lista fixa nossa) — carrega uma vez pra alimentar o seletor/rótulo
  useEffect(() => {
    api.get('/arquitetos/filiais').then(r => setFiliais(r.data)).catch(() => {});
  }, []);

  // Grava a filial do arquiteto (atualiza só a linha, sem recarregar tudo)
  async function alterarFilial(arq, codigo) {
    const filial = codigo || null;
    try {
      await api.patch(`/arquitetos/${arq.id}/filial`, { filial });
      setVinculados(prev => prev.map(a => a.id === arq.id ? { ...a, filial } : a));
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Não foi possível salvar a filial', text: e.response?.data?.error || 'Erro ao salvar', confirmButtonColor: VERDE });
    }
  }

  async function vincular(arq) {
    const emailErp = (arq.email_erp || '').trim();
    const senhaInicial = soDigitos(arq.CGC).slice(0, 6);
    const avisoErp = emailErp
      ? `<p class="mb-2" style="font-size:.78rem;text-align:left;color:#1F6A2D;">
           <i class="bi bi-check-circle-fill"></i> E-mail encontrado no Microuniverso — confira e ajuste se precisar.
         </p>`
      : `<p class="mb-2" style="font-size:.78rem;text-align:left;color:#C8860D;">
           <i class="bi bi-exclamation-triangle-fill"></i> Sem e-mail no Microuniverso. Pode informar agora ou deixar o arquiteto preencher no 1º acesso.
         </p>`;
    const { value: dados, isConfirmed } = await Swal.fire({
      title: `Vincular ${arq.nome}`,
      html: `
        <p class="text-muted mb-2" style="font-size:.88rem">CPF / CNPJ: ${arq.CGC || '—'}</p>
        <div style="background:#E6F5EA;border-radius:8px;padding:10px 12px;margin-bottom:10px;text-align:left;font-size:.82rem;color:#1F6A2D;">
          <i class="bi bi-key-fill"></i> Senha inicial: <strong>${senhaInicial || '—'}</strong>
          (6 primeiros dígitos do documento). O arquiteto confirma e-mail/WhatsApp e cria a senha no 1º acesso.
        </div>
        ${avisoErp}
        <input id="swal-email" type="email" class="swal2-input" placeholder="E-mail (opcional)" value="${emailErp.replace(/"/g, '&quot;')}" style="margin: 6px 0;">`,
      confirmButtonText: 'Vincular',
      confirmButtonColor: VERDE,
      cancelButtonText: 'Cancelar',
      showCancelButton: true,
      preConfirm: () => {
        const email = document.getElementById('swal-email').value.trim();
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          Swal.showValidationMessage('E-mail inválido'); return false;
        }
        return { email };
      },
    });

    if (!isConfirmed) return;

    try {
      const r = await api.post('/arquitetos/vincular', {
        oid: arq.oid, cgc: arq.CGC, nome: arq.nome,
        email: dados.email || null,
      });
      Swal.fire({
        icon: 'success',
        title: 'Arquiteto vinculado!',
        html: r.data?.email_convite
          ? 'Convite enviado por e-mail. A senha inicial são os <strong>6 primeiros dígitos do documento</strong>.'
          : 'Acesso criado. Avise o arquiteto: entra com o CPF e os <strong>6 primeiros dígitos</strong> como senha.',
        confirmButtonColor: VERDE,
      });
      carregarDisponiveis();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.response?.data?.error || 'Erro ao vincular', confirmButtonColor: VERDE });
    }
  }

  async function vincularEmMassa() {
    const candidatos = dispFiltrados; // respeita o filtro de busca atual
    const comEmail = candidatos.filter(a => a.email_erp).length;
    const { isConfirmed } = await Swal.fire({
      title: `Vincular ${candidatos.length} arquiteto(s)?`,
      html: `
        <div style="text-align:left;font-size:.9rem;line-height:1.6">
          Cada um recebe acesso com senha = <strong>6 primeiros dígitos do documento</strong>
          e confirma os dados no 1º acesso.<br><br>
          <i class="bi bi-info-circle" style="color:#1F6A2D"></i> <strong>Nenhum e-mail é enviado agora</strong> — você comunica a leva por fora (WhatsApp, presencial).<br>
          <i class="bi bi-envelope" style="color:#1F6A2D"></i> ${comEmail} têm e-mail cadastrado (dá pra comunicar por e-mail depois).
        </div>`,
      icon: 'question',
      confirmButtonText: `Vincular ${candidatos.length}`,
      confirmButtonColor: VERDE,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
    });
    if (!isConfirmed) return;

    Swal.fire({ title: 'Vinculando...', html: 'Aguarde, criando os acessos.', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const r = await api.post('/arquitetos/vincular-em-massa', {
        arquitetos: candidatos.map(a => ({ oid: a.oid, cgc: a.CGC, nome: a.nome, email: a.email_erp || null })),
      });
      const d = r.data;
      await Swal.fire({
        icon: 'success',
        title: 'Concluído!',
        html: `
          <div style="text-align:left;font-size:.9rem;line-height:1.7">
            <strong>${d.vinculados}</strong> de ${d.total} vinculados<br>
            <i class="bi bi-envelope" style="color:#1F6A2D"></i> ${d.com_email} têm e-mail cadastrado<br>
            ${d.falhas?.length ? `<i class="bi bi-x-circle" style="color:#B53D3D"></i> ${d.falhas.length} falha(s)` : ''}
          </div>
          <div style="text-align:left;font-size:.82rem;color:#888;margin-top:8px">Nenhum e-mail foi enviado — comunique a leva por fora.</div>`,
        confirmButtonColor: VERDE,
      });
      carregarDisponiveis();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.response?.data?.error || 'Erro ao vincular em massa', confirmButtonColor: VERDE });
    }
  }

  async function alterarAtivo(arq) {
    const novoAtivo = !arq.ativo;
    const acao = novoAtivo ? 'ativar' : 'desativar';

    const { isConfirmed } = await Swal.fire({
      title: `${novoAtivo ? 'Ativar' : 'Desativar'} arquiteto?`,
      text: arq.nome,
      icon: 'question',
      confirmButtonText: acao.charAt(0).toUpperCase() + acao.slice(1),
      confirmButtonColor: novoAtivo ? VERDE : VERMELHO,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
    });

    if (!isConfirmed) return;

    try {
      await api.patch(`/arquitetos/${arq.id}/ativo`, { ativo: novoAtivo });
      setVinculados(prev => prev.map(a => a.id === arq.id ? { ...a, ativo: novoAtivo } : a));
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.response?.data?.error || 'Erro ao alterar status', confirmButtonColor: VERDE });
    }
  }

  async function desvincular(arq) {
    const { isConfirmed } = await Swal.fire({
      title: `Desvincular ${arq.nome}?`,
      html: `Isso <strong>remove o acesso</strong> do arquiteto ao portal.<br/><br/>
             O histórico de saques é preservado, e ele <strong>volta a aparecer na aba "Disponíveis"</strong>.
             Quando você vincular de novo, ele recebe uma nova senha provisória por e-mail e troca a senha no primeiro acesso.`,
      icon: 'warning',
      confirmButtonText: 'Desvincular',
      confirmButtonColor: VERMELHO,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
    });

    if (!isConfirmed) return;

    try {
      await api.patch(`/arquitetos/${arq.id}/desvincular`);
      setVinculados(prev => prev.filter(a => a.id !== arq.id));
      Swal.fire({
        icon: 'success',
        title: 'Arquiteto desvinculado',
        text: `${arq.nome} voltou para a lista de disponíveis.`,
        confirmButtonColor: VERDE,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Não foi possível desvincular', text: e.response?.data?.error || 'Erro ao desvincular', confirmButtonColor: VERDE });
    }
  }

  async function resetarSenha(arq) {
    const { isConfirmed } = await Swal.fire({
      title: `Resetar senha de ${arq.nome}?`,
      html: arq.email
        ? `Uma nova senha provisória será gerada e enviada para <strong>${arq.email}</strong>.<br/>O arquiteto será obrigado a trocá-la no próximo login.`
        : `Este arquiteto <strong>não tem e-mail cadastrado</strong>. A nova senha provisória aparecerá na tela e você precisará repassar ao arquiteto manualmente.`,
      icon: 'question',
      confirmButtonText: 'Resetar senha',
      confirmButtonColor: LARANJA,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
    });

    if (!isConfirmed) return;

    try {
      const r = await api.post(`/arquitetos/${arq.id}/resetar-senha`);
      if (r.data?.email_enviado) {
        Swal.fire({
          icon: 'success',
          title: 'Senha resetada',
          text: `Nova senha enviada para ${arq.email}.`,
          confirmButtonColor: VERDE,
        });
      } else if (r.data?.senha_provisoria) {
        Swal.fire({
          icon: 'success',
          title: 'Senha resetada',
          html: `Repasse esta senha ao arquiteto (não conseguimos enviar por e-mail):<br/><br/>
                 <code style="background:#f5f9f5; padding:8px 16px; border-radius:6px; font-size:1.2rem; letter-spacing:0.1em;">${r.data.senha_provisoria}</code>`,
          confirmButtonColor: VERDE,
        });
      } else {
        Swal.fire({ icon: 'success', title: 'Senha resetada', confirmButtonColor: VERDE });
      }
      carregarVinculados();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.response?.data?.error || 'Erro ao resetar senha', confirmButtonColor: VERDE });
    }
  }

  async function editarEmail(arq) {
    const { value: email, isConfirmed } = await Swal.fire({
      title: `E-mail de ${arq.nome}`,
      input: 'email',
      inputValue: arq.email || '',
      inputPlaceholder: 'email@exemplo.com',
      showCancelButton: true,
      confirmButtonText: 'Salvar',
      confirmButtonColor: VERDE,
      cancelButtonText: 'Cancelar',
      footer: '<div id="erp-email-hint" style="font-size:.82rem;color:#888;">Buscando e-mail no Microuniverso…</div>',
      didOpen: () => {
        const hint = document.getElementById('erp-email-hint');
        api.get(`/arquitetos/${arq.id}/email-erp`)
          .then(r => {
            const erp = (r.data?.email_erp || '').trim();
            if (!erp) {
              hint.innerHTML = '<i class="bi bi-info-circle"></i> Sem e-mail no Microuniverso para este arquiteto.';
              return;
            }
            hint.innerHTML = `<i class="bi bi-envelope-check" style="color:#1F6A2D"></i> No Microuniverso: <strong>${erp}</strong>
              <button type="button" id="btn-puxar-erp" class="btn btn-sm btn-outline-success ms-2" style="padding:1px 8px;font-size:.78rem;">Usar este</button>`;
            document.getElementById('btn-puxar-erp')?.addEventListener('click', () => {
              const input = Swal.getInput();
              if (input) input.value = erp;
            });
          })
          .catch(() => { hint.innerHTML = ''; });
      },
      inputValidator: v => {
        if (!v) return null; // permite limpar
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'E-mail inválido';
      },
    });

    if (!isConfirmed) return;

    try {
      await api.patch(`/arquitetos/${arq.id}/email`, { email: email || null });
      setVinculados(prev => prev.map(a => a.id === arq.id ? { ...a, email: email || null } : a));
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.response?.data?.error || 'Erro ao salvar e-mail', confirmButtonColor: VERDE });
    }
  }

  const dispFiltrados = disponiveis.filter(a =>
    a.nome.toLowerCase().includes(busca.toLowerCase()) ||
    (a.CGC || '').includes(busca)
  );

  return (
    <div>
      <h4 className="fw-bold mb-1">
        <i className="bi bi-person-badge me-2" style={{ color: 'var(--primary)' }}></i>
        Arquitetos
      </h4>
      <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>Gerencie os arquitetos com acesso ao portal</p>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link ${aba === 'vinculados' ? 'active' : ''}`} onClick={() => setAba('vinculados')}>
            <i className="bi bi-people me-1"></i>Vinculados
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${aba === 'vincular' ? 'active' : ''}`} onClick={() => setAba('vincular')}>
            <i className="bi bi-person-plus me-1"></i>Vincular novo
          </button>
        </li>
      </ul>

      {aba === 'vinculados' && (
        <div className="card p-0">
          {carregando ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
            </div>
          ) : erro ? (
            <div className="alert alert-danger m-3">{erro}</div>
          ) : (
            <div className="tabela-container">
              <table className="table table-hover table-arq mb-0">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>CPF / CNPJ</th>
                    <th>E-mail</th>
                    <th>Status</th>
                    <th>Filial</th>
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {vinculados.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: 0 }}>
                      <EmptyState
                        icone="bi-person-plus"
                        titulo="Nenhum arquiteto vinculado ainda"
                        texto="Vá na aba 'Vincular novo' e busque o arquiteto pelo nome ou CPF."
                      />
                    </td></tr>
                  ) : vinculados.map(a => (
                    <tr key={a.id}>
                      <td className="fw-semibold">{a.nome}</td>
                      <td className="text-muted">{a.cgc || '—'}</td>
                      <td className="text-muted" style={{ fontSize: '0.88rem' }}>
                        {a.email ? a.email : <span style={{ color: LARANJA, fontStyle: 'italic' }}>Não cadastrado</span>}
                      </td>
                      <td>
                        <div className="d-flex gap-1 align-items-center flex-wrap">
                          <span className={a.ativo ? 'badge-positivo' : 'badge-neutro'}>
                            {a.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                          {a.senha_provisoria && (
                            <span className="badge-pendente" title="Senha provisória — arquiteto precisa trocar no próximo login">
                              Senha provisória
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {podeFilial ? (
                          <select
                            className="form-select form-select-sm"
                            style={{ minWidth: 140, fontSize: '0.85rem' }}
                            value={a.filial || ''}
                            onChange={(e) => alterarFilial(a, e.target.value)}
                            title="Filial de origem do arquiteto (preenchimento manual)"
                          >
                            <option value="">— definir —</option>
                            {filiais.map(f => (
                              <option key={f.codigo} value={f.codigo}>{f.nome}</option>
                            ))}
                          </select>
                        ) : (
                          nomeFilial(a.filial) || <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-end">
                        {podeEditar ? (
                          <MenuAcoes itens={[
                            {
                              label: a.email ? 'Alterar e-mail' : 'Cadastrar e-mail',
                              icone: 'bi-envelope',
                              onClick: () => editarEmail(a),
                            },
                            { label: 'Resetar senha', icone: 'bi-key', onClick: () => resetarSenha(a) },
                            { divisor: true },
                            {
                              label: a.ativo ? 'Desativar (suspende acesso)' : 'Ativar',
                              icone: a.ativo ? 'bi-pause-circle' : 'bi-play-circle',
                              aviso: a.ativo,
                              onClick: () => alterarAtivo(a),
                            },
                            {
                              label: 'Desvincular (remove acesso)',
                              icone: 'bi-person-dash',
                              perigo: true,
                              onClick: () => desvincular(a),
                            },
                          ]} />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {aba === 'vincular' && (
        <div>
          <div className="mb-3 d-flex gap-2 align-items-center flex-wrap">
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nome ou CPF/CNPJ..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              style={{ maxWidth: 400 }}
            />
            {dispFiltrados.length > 0 && (
              <button className="btn btn-primary-arq ms-auto" onClick={vincularEmMassa}>
                <i className="bi bi-people-fill me-1"></i>
                Vincular {busca ? `${dispFiltrados.length} filtrados` : 'todos'} ({dispFiltrados.length})
              </button>
            )}
          </div>
          <div className="card p-0">
            {carregando ? (
              <div className="text-center py-5">
                <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
              </div>
            ) : erro ? (
              <div className="alert alert-danger m-3">{erro}</div>
            ) : (
              <div className="tabela-container">
                <table className="table table-hover table-arq mb-0">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>CPF / CNPJ</th>
                      <th>E-mail (Microuniverso)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispFiltrados.length === 0 ? (
                      <tr><td colSpan={4} style={{ padding: 0 }}>
                        <EmptyState
                          icone="bi-search"
                          cor="neutro"
                          titulo={busca ? 'Nenhum arquiteto encontrado' : 'Sem candidatos disponíveis'}
                          texto={busca
                            ? 'Tente outro nome ou CPF.'
                            : 'Os arquitetos vêm da categoria correspondente no BDENTER. Confirme com o TI se algum deveria estar aqui.'}
                        />
                      </td></tr>
                    ) : dispFiltrados.map(a => (
                      <tr key={a.oid}>
                        <td className="fw-semibold">{a.nome}</td>
                        <td className="text-muted">{a.CGC || '—'}</td>
                        <td>
                          {a.email_erp ? (
                            <span style={{ color: '#1F6A2D', fontSize: '0.88rem' }}>
                              <i className="bi bi-envelope-check me-1"></i>{a.email_erp}
                            </span>
                          ) : (
                            <span className="badge-pendente" title="Sem e-mail no Microuniverso — informe no momento de vincular">
                              <i className="bi bi-exclamation-triangle me-1"></i>Sem e-mail
                            </span>
                          )}
                        </td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-primary-arq" onClick={() => vincular(a)}>
                            <i className="bi bi-person-plus me-1"></i>Vincular
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
